import { useEffect, useLayoutEffect, useReducer, useRef, useState } from "react"
import { lockedReducer } from "../../utils/defaultReducer"
import { Alert, Box, FormControl, IconButton, InputLabel, Menu, MenuItem, Select, TextField, Tooltip } from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { FileDrop } from 'react-file-drop';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import ImportIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import dayjs from 'dayjs'
import GroupAutocomplete from "../Autocomplete/Group";

export const initialState = {
    name: '',
    title: '',
    description: '',
    logo: '',
    logo_url: '',
    ga_measurement_id: '',
    success_message: '',
    groups: [],
    groups_changed: false,
    access_mode: 'public',
    opportunity_title_template: '',
    settings: {
        general: {},
        button: {}
    }
}

export const titleTemplateVariables = [
    { value: '{{contact_name}}', label: 'Nome do contato' },
    { value: '{{contact_email}}', label: 'E-mail do contato' },
    { value: '{{contact_telephone}}', label: 'Telefone do contato' },
    { value: '{{contact_cpf}}', label: 'CPF do contato' },
    { value: '{{contact_enterprise_name}}', label: 'Nome da empresa' },
    { value: '{{contact_enterprise_email}}', label: 'E-mail da empresa' },
    { value: '{{contact_enterprise_cnpj}}', label: 'CNPJ da empresa' },
    { value: '{{form_title}}', label: 'Título do formulário' },
    { value: '{{form_name}}', label: 'Nome do formulário' },
]

const WebFormsStep1 = ({ parent, setParent }) => {

    const [state, setState] = useReducer(lockedReducer, initialState)
    const titleTemplateRef = useRef(null)
    const [variableMenuAnchor, setVariableMenuAnchor] = useState(null)

    useLayoutEffect(() => {
        setState(parent)
    }, [])

    const insertTitleVariable = (variable) => {
        const input = titleTemplateRef.current
        const value = state.opportunity_title_template || ''
        const start = input ? input.selectionStart ?? value.length : value.length
        const end = input ? input.selectionEnd ?? value.length : value.length
        const newValue = value.slice(0, start) + variable + value.slice(end)

        setState({ opportunity_title_template: newValue })
        setVariableMenuAnchor(null)

        requestAnimationFrame(() => {
            if (!input) return
            const cursor = start + variable.length
            input.focus()
            input.setSelectionRange(cursor, cursor)
        })
    }

    useEffect(() => {
        //add debounce
        setParent(state)
        return () => {
            setParent(state)
        }
    }, [state])

    return (
        <Box display='flex' gap='1rem' height='100%' overflow='hidden' padding='.25rem'>
            <Box display='flex' flexDirection='column' gap='.75rem' flex={1} minHeight='0px' overflow='auto'>
                <TextField
                    label='Nome'
                    value={state.name}
                    size='small'
                    onChange={({ target }) => setState({ name: target.value })}
                />
                <TextField
                    label='Título'
                    value={state.title}
                    size='small'
                    onChange={({ target }) => setState({ title: target.value })}
                />
                <FormControl size='small'>
                    <InputLabel id='webform-access-mode-label'>Acesso</InputLabel>
                    <Select
                        labelId='webform-access-mode-label'
                        label='Acesso'
                        value={state.access_mode || 'public'}
                        onChange={({ target }) => setState({ access_mode: target.value })}
                    >
                        <MenuItem value='public'>Público (sem login)</MenuItem>
                        <MenuItem value='internal'>Interno (somente usuários logados)</MenuItem>
                    </Select>
                </FormControl>
                <TextField
                    label='Título da oportunidade'
                    value={state.opportunity_title_template}
                    size='small'
                    placeholder='Deixe em branco para usar o padrão'
                    onChange={({ target }) => setState({ opportunity_title_template: target.value })}
                    inputRef={titleTemplateRef}
                    InputProps={{
                        endAdornment: (
                            <Tooltip title='Inserir variável'>
                                <IconButton
                                    size='small'
                                    onClick={({ currentTarget }) => setVariableMenuAnchor(currentTarget)}
                                >
                                    <AddCircleOutlineIcon fontSize='small' />
                                </IconButton>
                            </Tooltip>
                        )
                    }}
                />
                <Menu
                    anchorEl={variableMenuAnchor}
                    open={Boolean(variableMenuAnchor)}
                    onClose={() => setVariableMenuAnchor(null)}
                >
                    {titleTemplateVariables.map((variable) => (
                        <MenuItem key={variable.value} onClick={() => insertTitleVariable(variable.value)}>
                            {variable.label} — <span style={{ color: 'gray', marginLeft: '.25rem' }}>{variable.value}</span>
                        </MenuItem>
                    ))}
                </Menu>
                <TextField
                    multiline
                    minRows={4}
                    maxRows={4}
                    label='Descrição'
                    value={state.description}
                    onChange={({ target }) => setState({ description: target.value })}
                    size='small'
                />
                <div className="file-drop-container">
                    <FileDrop
                        onDrop={(files, event) => {
                            if (state.logo_url) return
                            setState({ logo: files[0] })
                        }}
                    >
                        {state.logo ? <span>{state.logo.name}
                            <IconButton className={'action-button delete'}>
                                <DeleteIcon onClick={() => setState({ logo: null })} />
                            </IconButton>
                        </span> :
                            <>{!state.logo_url && <span>Arraste e solte para carregar a logo.</span>}</>
                        }

                        <>{!state.logo_url && <span>ou</span>} </>
                        <LoadingButton
                            variant="contained"
                            color="primary"
                            component="label"
                            loading={false}
                            loadingPosition="start"
                            startIcon={<ImportIcon />}
                            disabled={state.logo_url}
                            onClick={() => { }}
                        >
                            Selecione o arquivo.
                            <input
                                hidden

                                type="file"
                                accept={'.png, .jpg, .jpeg'}
                                onChange={(e) => { setState({ logo: e.target.files[0] }) }}
                            />

                        </LoadingButton>
                        <span style={{ margin: "8px 0px" }}>ou</span>
                        <Tooltip
                            title={`Alternativamente ao arquivo, coloque a URL da mídia`}
                        >
                            <TextField
                                label="URL"
                                size="small"
                                disabled={state.logo}
                                value={state.logo_url}
                                onChange={({ target }) => setState({ logo_url: target.value })}
                            />
                        </Tooltip>
                    </FileDrop>
                </div>

                <TextField
                    label='ID Google Analytics'
                    value={state.ga_measurement_id}
                    onChange={({ target }) => setState({ ga_measurement_id: target.value })}
                    size='small'
                />
                <TextField
                    multiline
                    maxRows={4}
                    label='Mensagem de sucesso'
                    value={state.success_message}
                    onChange={({ target }) => setState({ success_message: target.value })}
                    size='small'
                    inputProps={{
                        maxLength: 1024
                    }}
                />
                <GroupAutocomplete
                    sx={{padding: 0}}
                    customLabel='Segmentos'
                    selectedGroup={[
                        ...new Map(
                            [...state.groups].map(item => [item.id, item])
                        ).values()
                    ]}
                    onChange={(data) => setState({
                        groups: data,
                        groups_changed: true
                    })}
                    disablePortal={true}
                    multiple={true}
                />
            </Box>
            <Box flex={1} minWidth='0px' overflow='auto'>
                <Alert severity='info' sx={{ height: '100%', whiteSpace: 'pre-line', overflow: 'auto', fontSize: '0.75rem' }}>
                    {
                        `
📝 Criação de Formulário Dinâmico
Configure as informações iniciais do seu formulário para definir identidade, acompanhamento e comportamento após o envio.

🖼️ Logo do Formulário
Envie uma imagem para representar visualmente o formulário. A logo aparece no topo da página e ajuda a reforçar a identidade da marca ou campanha.

📊 Google Analytics
Insira o ID de acompanhamento para enviar dados de acesso e interação diretamente para sua conta do Google Analytics, permitindo monitorar visualizações e taxa de conclusão.

✅ Mensagem de Sucesso
Personalize o texto exibido ao usuário após o envio. Use esse espaço para confirmar o recebimento, agradecer ou orientar o próximo passo.

👥Segmentos
Todas as empresas ou contatos que preencherem o formulário pertencerão aos segmentos selecionados.

📌 Criação automática de oportunidades
Cada resposta registrada no formulário gerará uma oportunidade na primeira coluna. Na ausência de colunas configuradas, o sistema criará automaticamente uma nova coluna.

🏷️ Título da oportunidade
Personalize o título da oportunidade criada usando variáveis como {{contact_name}} ou {{contact_enterprise_name}}. Se deixado em branco, usa o padrão (título do formulário + nome do contato).

🔒 Acesso
Formulários públicos podem ser preenchidos por qualquer pessoa via link/embed. Formulários internos só podem ser preenchidos por usuários logados desta empresa e usam a busca do CRM para selecionar contato/empresa.
                    `
                    }
                </Alert>
            </Box>
        </Box>
    )
}

export default WebFormsStep1